/**
 * Deal-type comparison helpers.
 *
 * The backend stores `deal_type` lowercase (`bhph` | `cash` | `outside_finance`)
 * per the CaseInsensitiveEnum convention, but legacy rows and older API payloads
 * may still carry UPPERCASE labels. Compare through these helpers so a casing
 * change on either side can never silently hide a UI control again.
 */

export type DealTypeKey = 'BHPH' | 'CASH' | 'OUTSIDE_FINANCE';

/** Legacy label kept in sync with the backend's deal_contracting normalization. */
const LEGACY_ALIASES: Record<string, DealTypeKey> = {
  FINANCE: 'OUTSIDE_FINANCE',
  FINANCING: 'OUTSIDE_FINANCE',
};

export function normalizeDealType(dealType?: string | null): DealTypeKey | '' {
  if (!dealType) return '';
  const key = dealType.trim().toUpperCase();
  return LEGACY_ALIASES[key] ?? (key as DealTypeKey);
}

export function isDealType(dealType: string | null | undefined, expected: DealTypeKey): boolean {
  return normalizeDealType(dealType) === expected;
}

export const isBhphDeal = (dealType?: string | null): boolean => isDealType(dealType, 'BHPH');

export const isCashDeal = (dealType?: string | null): boolean => isDealType(dealType, 'CASH');

/** Cash deals carry no financing; everything else does. */
export const isFinancedDeal = (dealType?: string | null): boolean => !isCashDeal(dealType);

const DEAL_TYPE_LABELS: Record<DealTypeKey, string> = {
  BHPH: 'BHPH',
  CASH: 'Cash',
  OUTSIDE_FINANCE: 'Outside Finance',
};

/** Human-facing label — never render the raw stored value, it is lowercase. */
export function formatDealType(dealType?: string | null): string {
  const key = normalizeDealType(dealType);
  if (!key) return '—';
  return DEAL_TYPE_LABELS[key] ?? key;
}
